// Paketwahl der Payment-Seite. Ein Paket ist eine Vorlage, Zahlen und
// Produkte bleiben danach frei editierbar. Single-Select im RadioGroup-Muster
// der Seite (HeadlessUI v1-Punkt-API).
import { RadioGroup } from '@headlessui/react'
import { CheckCircleIcon } from '@heroicons/react/20/solid'

import { CUSTOM_PLAN, PLANS } from '../../constants/pricing-catalog'
import { classNames } from '../../helper'
import {
  PLAN_CHOICES,
  PLAN_NAMES,
  formatCount,
  listPrice
} from '../../helper/pricing'

// Wert der Karte „Kein Paket". RadioGroup braucht einen String, null hieße
// nichts gewählt.
export const NO_PLAN_CHOICE = 'none'

// Kurzfassung je Paket, abgeglichen mit PRODUCT_AVAILABILITY im Katalog.
// Ändert sich der Katalog, hier nachziehen (Test in PlanPresetSelector.test.js).
export const PLAN_CARD_TEXTS = {
  start:
    'Für den Einstieg. ATS-Plugin zubuchbar, ohne Partner-ATS, Kreuzmatching und Rollenrechte.',
  grow: 'Mit Kreuzmatching, Orientierungsmodul, Partner-ATS, Rollenrechten, Unterdashboards und DSGVO-Automatisierung.',
  scale:
    'Zusätzlich anpassbare Ergebnisreports, Assessment auf Französisch und Niederländisch sowie eine feste Ansprechperson.',
  unlimited:
    'Für hohe Volumen. Individueller Vertrag und Custom API inklusive.',
  [CUSTOM_PLAN]:
    'Eigene Zusammenstellung. Die Pricing-Sperre greift mit genau diesen Werten.',
  [NO_PLAN_CHOICE]:
    'Für Trials und Altverträge. Das Dashboard rechnet wie heute, die Pricing-Sperre greift nicht.'
}

export const NO_PLAN_NAME = 'Kein Paket (Trial oder Altvertrag)'

export const formatEuro = (amount) => `${formatCount(amount)} €`

/**
 * Limits eines Pakets in einer Zeile, z. B. „20 Stellen (bis 38), 10 Zugänge
 * (beliebig zubuchbar), 2.000 Talente".
 */
export const planLimitsLine = (choice) => {
  if (choice === 'unlimited') {
    return 'Stellen, Zugänge und Talentspeicher unbegrenzt'
  }
  if (choice === CUSTOM_PLAN) return 'Zahlen und Produkte frei wählbar'
  if (choice === NO_PLAN_CHOICE) return 'Zahlen und Produkte wie bisher'

  const plan = PLANS[choice]
  if (!plan) return ''

  const { careers, seats, talentStorage } = plan.limits
  const seatsMax = Number.isFinite(plan.max.seats)
    ? `bis ${formatCount(plan.max.seats)}`
    : 'beliebig zubuchbar'

  return [
    `${formatCount(careers)} Stellen (bis ${formatCount(plan.max.careers)})`,
    `${formatCount(seats)} Zugänge (${seatsMax})`,
    `${formatCount(talentStorage)} Talente`
  ].join(', ')
}

/**
 * Listenpreis als Hinweis auf der Karte. Gespeichert wird der Preis nie
 * automatisch.
 */
export const planPriceHint = (choice) => {
  if (choice === CUSTOM_PLAN) return 'Preis nach Vereinbarung'

  const price = listPrice(choice)
  if (!price) return null

  const text = `Listenpreis ${formatEuro(price.monthly)} / Monat bei 12 Monaten`
  return typeof price.flexMonthly === 'number'
    ? `${text}, monatlich kündbar ${formatEuro(price.flexMonthly)}`
    : text
}

export const choiceName = (choice) =>
  choice === NO_PLAN_CHOICE ? NO_PLAN_NAME : PLAN_NAMES[choice] || choice

/**
 * @param {object} props
 * @param {string|null} props.value Paket-ID, NO_PLAN_CHOICE oder null
 * @param {(choice: string) => void} props.onChange
 * @param {boolean} [props.allowNoPlan] zeigt „Kein Paket" (nur beim Anlegen)
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.customized] Badge „Angepasst" an der gewählten Karte
 * @param {string} [props.error]
 */
// Verknüpft die Fehlermeldung mit der Gruppe (aria-describedby)
export const PLAN_ERROR_ID = 'plan-preset-error'

const PlanPresetSelector = ({
  value = null,
  onChange,
  allowNoPlan = false,
  disabled = false,
  customized = false,
  error
}) => {
  const choices = allowNoPlan ? [...PLAN_CHOICES, NO_PLAN_CHOICE] : PLAN_CHOICES

  return (
    <div>
      <RadioGroup
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label='Paket'
        aria-invalid={error ? true : undefined}
      >
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {choices.map((choice) => {
            const priceHint = planPriceHint(choice)
            const selected = value === choice

            return (
              <RadioGroup.Option
                key={choice}
                value={choice}
                data-testid={`plan-card-${choice}`}
                className={({ checked }) =>
                  classNames(
                    checked
                      ? 'border-indigo-600 ring-2 ring-indigo-600'
                      : 'border-gray-300',
                    disabled
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer',
                    'relative flex rounded-lg border bg-white p-4 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2'
                  )
                }
              >
                {({ checked }) => (
                  <>
                    <span className='flex flex-1 flex-col'>
                      <span className='flex items-center gap-x-2'>
                        <RadioGroup.Label
                          as='span'
                          className='block text-sm font-semibold text-gray-900'
                        >
                          {choiceName(choice)}
                        </RadioGroup.Label>
                        {selected && customized && (
                          <span className='inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20'>
                            Angepasst
                          </span>
                        )}
                      </span>
                      <RadioGroup.Description
                        as='span'
                        className='mt-1 text-sm font-medium text-gray-700'
                      >
                        {planLimitsLine(choice)}
                      </RadioGroup.Description>
                      <span className='mt-1 text-sm text-gray-500'>
                        {PLAN_CARD_TEXTS[choice]}
                      </span>
                      {priceHint && (
                        <span className='mt-2 text-xs text-gray-500'>
                          {priceHint}
                        </span>
                      )}
                    </span>
                    <CheckCircleIcon
                      className={classNames(
                        checked ? '' : 'invisible',
                        'h-5 w-5 flex-shrink-0 text-indigo-600'
                      )}
                      aria-hidden='true'
                    />
                  </>
                )}
              </RadioGroup.Option>
            )
          })}
        </div>
        {/* Innerhalb der Gruppe, damit Headless UI sie per
            aria-describedby mit der Gruppe verknüpft. Ein eigenes
            aria-describedby an RadioGroup überschreibt Headless UI. */}
        {error && (
          <RadioGroup.Description
            as='p'
            id={PLAN_ERROR_ID}
            className='mt-2 text-sm text-red-600'
          >
            {error}
          </RadioGroup.Description>
        )}
      </RadioGroup>
    </div>
  )
}

export default PlanPresetSelector
